import { useCallback, useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/common/PageHeader";
import CampoData from "@/components/common/CampoData";
import { StatusBadge } from "@/components/ui/status-badge";
import EmptyStateSection from "@/components/common/EmptyStateSection";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMedia } from "@/hooks/use-media";
import { cn } from "@/lib/utils";
import { giorno as giornoLungo, quando } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { euroPreciso, giornoScadenza, giorniTra, leggiImporto, messaggioErrore } from "@/lib/preventivi";
import { oggiRoma } from "@/lib/soldi";
import { registraSalvataggio } from "@/lib/salvataggi";
import {
  FileWarning,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  ClipboardCheck,
  PenSquare,
  ArrowRight,
  Check,
  Copy,
  Eye,
} from "lucide-react";
import {
  TipoFornituraSelector,
  PuntiDistintiviBlock,
  RoadmapBlock,
  OmaggiBlock,
  PagamentiBlock,
  RecensioneBlock,
} from "./BlocchiFiduciaPagamenti";
import {
  RegistroSelector,
  PubblicaPreventivoButton,
} from "./PubblicaPreventivoButton";
import { eur } from "@/lib/formato";
import { useConferma } from "@/hooks/use-conferma";


const GRUPPI = [
  { id: "riconoscimento", label: "Riconoscimento" },
  { id: "valore", label: "Valore" },
  { id: "fiducia", label: "Fiducia" },
  { id: "decisione", label: "Decisione" },
];

const NOME_STATO = { bozza: "Bozza", da_rivedere: "Da rivedere", approvato: "Approvato", inviato: "Inviato" };
const TONO_STATO = { bozza: "neutral", da_rivedere: "warning", approvato: "success", inviato: "brand" };
const ORE_24 = 24 * 3600 * 1000;

const nomeCliente = (c) =>
  c ? c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || null : null;

/* «A che punto è»: Bozza → Approvato → Inviato → Aperto → Confermato. Aperto e Confermato si sanno solo a
   preventivo inviato, dal link. */
function Passi({ passo, primo }) {
  const nomi = [primo, "Approvato", "Inviato", "Aperto", "Confermato"];
  return (
    <ol aria-label="A che punto è" className="flex flex-wrap items-center gap-x-2 gap-y-2" data-testid="editor-passi">
      {nomi.map((n, i) => {
        const fatto = i < passo;
        const ora = i === passo;
        return (
          <li key={n} aria-current={ora ? "step" : undefined} className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex size-6 items-center justify-center rounded-full text-caption font-bold",
                fatto ? "bg-brand text-on-brand" : ora ? "border-2 border-brand text-brand" : "border border-line-input text-ink-3",
              )}
              aria-hidden
            >
              {fatto ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
            </span>
            <span className={cn(ora ? "font-bold text-ink-strong" : fatto ? "font-medium text-ink" : "text-ink-3")}>
              {n}
              <span className="sr-only">{fatto ? ", fatto" : ora ? ", adesso" : ", dopo"}</span>
            </span>
            {i < nomi.length - 1 ? <span className="h-px w-4 bg-line-input max-md:hidden" aria-hidden /> : null}
          </li>
        );
      })}
    </ol>
  );
}

/* L'anteprima è il documento vero, composto dal server come lo vedrà il cliente (stesso HTML della pubblicazione),
   mostrato in un iframe senza script. Si rilegge quando si salva in pagina; i blocchi che si salvano da soli
   (punti distintivi, omaggi…) si vedono con «Aggiorna». */
function Anteprima({ id, rev, nome }) {
  const [html, setHtml] = useState(null);
  const [errore, setErrore] = useState(null);
  const [carico, setCarico] = useState(true);
  const carica = useCallback(() => {
    setCarico(true);
    Promise.resolve(api.get(`/preventivatore/${id}/anteprima`, { responseType: "text" }))
      .then((r) => {
        setHtml(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore(messaggioErrore(e, "L'anteprima non si è caricata: premi «Aggiorna».")))
      .finally(() => setCarico(false));
  }, [id]);
  useEffect(() => {
    const t = setTimeout(carica, 500);
    return () => clearTimeout(t);
  }, [carica, rev]);
  return (
    <div className="flex flex-col gap-3" data-testid="editor-anteprima">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-h3 text-ink-strong">{nome ? `Come lo vede ${nome}` : "Come lo vede il cliente"}</h2>
        <Button size="sm" variant="outline" onClick={carica} disabled={carico} data-testid="anteprima-aggiorna">
          <RefreshCw className={cn(carico && "animate-spin motion-reduce:animate-none")} aria-hidden />
          Aggiorna
        </Button>
      </div>
      {errore ? (
        <p role="status" className="rounded-md border border-warning-line bg-warning-bg p-3 text-small text-warning-ink" data-testid="anteprima-errore">
          {errore}
        </p>
      ) : null}
      {html ? (
        <iframe
          title="Anteprima del documento"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={html}
          className="h-[640px] w-full rounded-lg border border-line bg-white"
          data-testid="anteprima-frame"
        />
      ) : !errore ? (
        <div className="h-[640px] w-full animate-ida-skeleton rounded-lg bg-surface-3" aria-hidden />
      ) : null}
      <p className="text-small text-ink-2">È il documento come lo vedrà il cliente. Da qui il bottone «Confermo» non fa niente e non conta come un'apertura.</p>
    </div>
  );
}


/* Il link del cliente, a preventivo inviato: quante volte l'ha aperto, l'ultima volta e da che dispositivo, se ha
   confermato. Tutto viene dal server (aperture senza i robot di anteprima). Niente durata della lettura: il
   documento pubblico non ha JavaScript. */
function PannelloLink({ link, ricarica }) {
  if (link === undefined) return <div className="h-40 animate-ida-skeleton rounded-xl bg-surface-3" aria-hidden />;
  if (link === null)
    return (
      <section className="rounded-xl border border-line bg-surface p-4 md:p-5" data-testid="inviato-link-pannello">
        <h2 className="font-display text-h3 text-ink-strong">Il link</h2>
        <p className="mt-2 text-ink-2">Non riesco a leggere il link di questo preventivo.</p>
        <Button className="mt-3" variant="outline" onClick={ricarica}>Riprova</Button>
      </section>
    );
  const n = link.aperture || 0;
  const ultima = link.ultima_apertura ? new Date(link.ultima_apertura) : null;
  const recente = ultima && Date.now() - ultima.getTime() < ORE_24;
  const stato = link.stato;
  let titolo;
  if (stato === "revocato") titolo = "Il link è stato revocato";
  else if (stato === "scaduto") titolo = "Il link è scaduto";
  else if (!n) titolo = "Il cliente non l'ha ancora aperto";
  else titolo = `Aperto ${n === 1 ? "una volta" : `${n} volte`}, l'ultima ${quando(ultima)}`;
  const dove = link.dispositivo ? ` dal ${link.dispositivo === "telefono" ? "telefono" : link.dispositivo === "tablet" ? "tablet" : "computer"}` : "";
  const dettaglio = [];
  if (ultima && n) dettaglio.push(`${quando(ultima, { conOra: true })}${dove}.`.replace(/^./, (c) => c.toUpperCase()));
  let confermato = null;
  if (link.confermato_il) confermato = `Confermato dal link ${quando(new Date(link.confermato_il), { conOra: true })}.`;
  // «Non ha ancora confermato» si dice solo se la conferma si è letta davvero (completo !== false).
  else if (recente && stato === "attivo" && link.completo !== false) dettaglio.push("Non ha ancora confermato: è un buon momento per sentirla.");
  const scad = giornoScadenza(link.scadenza);
  const gg = scad ? giorniTra(oggiRoma(), String(link.scadenza).slice(0, 10)) : null;
  let frase = null;
  if (scad && stato === "attivo") frase = `Scade ${giornoLungo(scad)}${gg === 0 ? ", oggi" : `, tra ${gg === 1 ? "un giorno" : `${gg} giorni`}`}. Dopo, il link non si apre più.`;
  else if (scad && stato === "scaduto") frase = `Scaduto ${giornoLungo(scad)}.`;
  return (
    <section className="rounded-xl border border-line bg-surface p-4 md:p-5" data-testid="inviato-link-pannello" aria-labelledby="inviato-link-titolo">
      <h2 id="inviato-link-titolo" className="font-display text-h3 text-ink-strong" data-testid="inviato-aperture">
        {titolo}
      </h2>
      {dettaglio.length ? <p className="mt-1 text-ink-2" data-testid="inviato-ultima">{dettaglio.join(" ")}</p> : null}
      {confermato ? (
        <p className="mt-2 flex items-center gap-2 font-semibold text-success-ink" data-testid="inviato-confermato">
          <Check className="size-4" aria-hidden /> {confermato}
        </p>
      ) : null}
      {link.link_pubblico ? (
        <div className="mt-4 flex items-center gap-2">
          <Input readOnly value={link.link_pubblico} aria-label="Link del cliente" data-testid="inviato-link-url" />
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard?.writeText(link.link_pubblico);
              toast.success("Link copiato");
            }}
            data-testid="inviato-link-copia"
          >
            <Copy aria-hidden /> Copia
          </Button>
        </div>
      ) : null}
      {frase ? (
        <p className="mt-3 text-small text-ink-2" data-testid="inviato-scadenza">
          {frase}
        </p>
      ) : null}
    </section>
  );
}

/* ================================================================= */
export default function PreventivatoreEditorPage() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [tab, setTab] = useState(null);
  // Senza questo, un caricamento fallito lasciava la pagina su "Caricamento…"
  // per sempre: il toast spariva dopo pochi secondi e restava una parola grigia.
  const [errore, setErrore] = useState(null);

  const readOnly = detail?.preventivo?.stato === "inviato";
  const [cliente, setCliente] = useState(null);
  const [link, setLink] = useState(undefined); // undefined = carico · null = non si legge
  const [rev, setRev] = useState(0);
  const [mostraBlocchi, setMostraBlocchi] = useState(false);
  const [anteprimaAperta, setAnteprimaAperta] = useState(false);
  const largo = useMedia("(min-width: 1280px)");

  const load = useCallback(async () => {
    setErrore(null);
    try {
      const [d, c] = await Promise.all([
        api.get(`/preventivatore/${id}`),
        api.get("/preventivatore/catalogo-blocchi"),
      ]);
      setDetail(d.data);
      setCatalogo(c.data);
      const t = await api.get(`/preventivatore/${id}/tabella-prezzi`);
      setTab(t.data);
    } catch (e) {
      const msg = messaggioErrore(e);
      setErrore(msg);
      toast.error(msg);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  // Il nome del cliente nel titolo: se non si legge, il titolo resta quello del preventivo.
  const clienteId = detail?.preventivo?.cliente_id;
  useEffect(() => {
    if (!clienteId) return undefined;
    let vivo = true;
    Promise.resolve(api.get("/clienti"))
      .then((r) => vivo && setCliente((r.data || []).find((c) => c.id === clienteId) || null))
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [clienteId]);

  // Il link del cliente (aperture, dispositivo, conferma) si legge solo a preventivo inviato.
  const caricaLink = useCallback(() => {
    setLink(undefined);
    Promise.resolve(api.get(`/preventivatore/${id}/link`))
      .then((r) => setLink(r.data?.esiste ? r.data : null))
      .catch(() => setLink(null));
  }, [id]);
  useEffect(() => {
    if (readOnly) caricaLink();
  }, [readOnly, caricaLink, rev]);

  const patchPreventivo = async (patch) => {
    try {
      const r = await registraSalvataggio(api.patch(`/preventivatore/${id}`, patch));
      setDetail((d) => ({ ...d, preventivo: r.data }));
      setRev((n) => n + 1);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const patchBlocco = async (codice, patch) => {
    try {
      const r = await registraSalvataggio(api.patch(`/preventivatore/${id}/blocco/${codice}`, patch));
      setDetail((d) => ({
        ...d,
        blocchi: d.blocchi.map((b) => (b.tipo === codice ? r.data : b)),
      }));
      setRev((n) => n + 1);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const transizione = async (target) => {
    try {
      const r = await registraSalvataggio(api.post(`/preventivatore/${id}/stato/${target}`, {}));
      setDetail((d) => ({ ...d, preventivo: r.data }));
      toast.success(`Stato aggiornato: ${NOME_STATO[r.data.stato] || r.data.stato}`);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const refreshMatrix = async () => {
    const t = await api.get(`/preventivatore/${id}/tabella-prezzi`);
    setTab(t.data);
    setRev((n) => n + 1);
  };

  if (errore) {
    return (
      <EmptyStateSection
        testId="editor-errore"
        icon={FileWarning}
        title="Questo preventivo non si è aperto"
        description={`Il server ha risposto: ${errore}`}
        action={
          <Button onClick={load} data-testid="editor-riprova">
            Riprova
          </Button>
        }
        secondary={
          <Button variant="outline" asChild data-testid="editor-torna-elenco">
            <Link to="/preventivi">Torna ai preventivi</Link>
          </Button>
        }
      />
    );
  }

  if (!detail || !catalogo.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="editor-caricamento">
        Caricamento…
      </p>
    );
  }

  const prev = detail.preventivo;
  const blocchiPerCodice = Object.fromEntries(detail.blocchi.map((b) => [b.tipo, b]));
  const nomeDelCliente = nomeCliente(cliente);
  const passo =
    prev.stato === "inviato"
      ? link?.confermato_il
        ? 4
        : link?.aperture > 0
          ? 3
          : 2
      : prev.stato === "approvato"
        ? 1
        : 0;

  const gruppiNoti = new Set(GRUPPI.map((g) => g.id));
  const blocchi = [...GRUPPI, { id: "altri", label: "Altri blocchi" }].map((g) => {
    const dentro = catalogo.filter((b) => (g.id === "altri" ? !gruppiNoti.has(b.gruppo) : b.gruppo === g.id));
    if (!dentro.length) return null;
    return (
      <div key={g.id} className="flex flex-col gap-3" data-testid={`gruppo-${g.id}`}>
        <h2 className="font-display text-h3 text-ink-strong">{g.label}</h2>
        {dentro.map((b) => {
          const row = blocchiPerCodice[b.codice];
          return (
            <BloccoCard
              key={b.codice}
              def={b}
              row={row}
              readOnly={readOnly}
              onToggle={(v) => patchBlocco(b.codice, { attivo: v })}
              onContenutoChange={(cont) => patchBlocco(b.codice, { contenuto: cont })}
            >
              {b.codice === "prezzo" ? (
                <PrezzoBlockContent
                  preventivoId={id}
                  detail={detail}
                  tab={tab}
                  onChanged={async () => {
                    // ricarica dettaglio + tabella
                    const d = await api.get(`/preventivatore/${id}`);
                    setDetail(d.data);
                    await refreshMatrix();
                  }}
                  readOnly={readOnly}
                />
              ) : null}
              {b.codice === "scadenza" ? (
                <ScadenzaBlockContent prev={prev} onPatch={patchPreventivo} readOnly={readOnly} />
              ) : null}
              {b.codice === "footer" ? <FooterBlockContent prev={prev} /> : null}
              {b.codice === "composizione" ? (
                <ComposizioneBlockContent
                  contenuto={row?.contenuto}
                  onSave={(c) => patchBlocco(b.codice, { contenuto: c })}
                  readOnly={readOnly}
                />
              ) : null}
              {b.codice === "scheda" ? (
                <SchedaBlockContent prev={prev} onPatch={patchPreventivo} readOnly={readOnly}
                  preventivoId={id} onTipoChanged={load} />
              ) : null}
              {b.codice === "progettista" ? (
                <ProgettistaBlockContent prev={prev} onPatch={patchPreventivo} readOnly={readOnly} />
              ) : null}
              {b.codice === "punti_distintivi" ? (
                <PuntiDistintiviBlock preventivoId={id} readOnly={readOnly} />
              ) : null}
              {b.codice === "omaggi_incluso" ? (
                <OmaggiBlock key={prev.tipo_fornitura} preventivoId={id} readOnly={readOnly} />
              ) : null}
              {b.codice === "roadmap" ? (
                <RoadmapBlock key={prev.tipo_fornitura} preventivoId={id} readOnly={readOnly} />
              ) : null}
              {b.codice === "pagamenti" ? (
                <PagamentiBlock
                  key={prev.tipo_fornitura}
                  preventivoId={id}
                  tipoFornitura={prev.tipo_fornitura}
                  totaleIndicativo={tab?.righe?.length
                    ? Math.max(...tab.righe.map((r) => r.totale || 0))
                    : null}
                  readOnly={readOnly}
                />
              ) : null}
              {b.codice === "perche_noi" ? (
                <RecensioneBlock preventivoId={id} readOnly={readOnly} />
              ) : null}
            </BloccoCard>
          );
        })}
      </div>
    );
  });

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-preventivatore-editor">
      <PageHeader
        back={{
          to: "/preventivi",
          label: "Preventivi",
          testId: "btn-back-to-list",
        }}
        title={nomeDelCliente ? `${prev.titolo || "Preventivo"} · ${nomeDelCliente}` : prev.titolo || "Preventivo"}
        description={`Rif. ordine ${prev.riferimento_ordine || "—"}`}
        actions={
          <>
            <StatusBadge tone={TONO_STATO[prev.stato]} data-testid="stato-badge">
              {NOME_STATO[prev.stato] || prev.stato}
            </StatusBadge>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {prev.stato === "bozza" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => transizione("da-rivedere")} data-testid="btn-da-rivedere">
                    <ClipboardCheck /> Manda a rivedere
                  </Button>
                  <Button size="sm" onClick={() => transizione("approvato")} data-testid="btn-approva">
                    <CheckCircle2 /> Approva
                  </Button>
                </>
              )}
              {prev.stato === "da_rivedere" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => transizione("bozza")} data-testid="btn-riporta-bozza">
                    <PenSquare /> Riporta in bozza
                  </Button>
                  <Button size="sm" onClick={() => transizione("approvato")} data-testid="btn-approva-2">
                    <CheckCircle2 /> Approva
                  </Button>
                </>
              )}
              {prev.stato === "approvato" && (
                <Button size="sm" variant="outline" onClick={() => transizione("bozza")} data-testid="btn-riporta-bozza-2">
                  <PenSquare /> Riporta in bozza
                </Button>
              )}
              {/* UN solo PubblicaPreventivoButton, sempre nello stesso posto per
                  «approvato» e «inviato». Prima ce n'erano due in rami diversi:
                  pubblicando, lo stato passava a «inviato», React montava il
                  secondo e smontava il primo — e con lui il dialogo del link,
                  appena aperto. Restava il messaggio «il link è pronto» e il
                  link non si vedeva. Difetto della call del 23-09. */}
              {(prev.stato === "approvato" || prev.stato === "inviato") && (
                <PubblicaPreventivoButton
                  preventivoId={id}
                  statoPreventivo={prev.stato}
                  onPublished={() => {
                    load();
                    setRev((n) => n + 1);
                  }}
                />
              )}
              {!readOnly && !largo ? (
                <Button size="sm" variant="outline" onClick={() => setAnteprimaAperta(true)} data-testid="btn-anteprima">
                  <Eye /> Anteprima
                </Button>
              ) : null}
            </div>
          </>
        }
      />

      <Passi passo={passo} primo={prev.stato === "da_rivedere" ? "Da rivedere" : "Bozza"} />

      {readOnly ? (
        <div className="flex flex-col gap-card" data-testid="editor-inviato">
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-card xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <PannelloLink link={link} ricarica={caricaLink} />
            <section className="rounded-xl border border-line bg-surface p-4 md:p-5" data-testid="inviato-proposta">
              <h2 className="font-display text-h3 text-ink-strong">
                {nomeDelCliente ? `Cosa abbiamo proposto a ${nomeDelCliente}` : "Cosa abbiamo proposto"}
              </h2>
              {tab?.righe?.length ? (
                <ul className="mt-3 divide-y divide-line-subtle">
                  {tab.righe.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                      <span className="min-w-0 truncate text-ink">{r.etichetta}</span>
                      <span className="shrink-0 font-bold tabular-nums text-ink-strong">{euroPreciso(r.totale)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-ink-2">Nessuna configurazione nel documento.</p>
              )}
              {prev.inviato_at ? (
                <p className="mt-3 text-small text-ink-2">Inviato {quando(new Date(prev.inviato_at))}.</p>
              ) : null}
              <p className="mt-3 text-small text-ink-2">
                Un preventivo inviato non si modifica: per cambiare qualcosa serve un preventivo nuovo. «Rifai il link» manda lo stesso documento con un indirizzo nuovo, e il vecchio smette di funzionare.
              </p>
            </section>
          </div>
          <div>
            <Button variant="outline" onClick={() => setMostraBlocchi((v) => !v)} aria-expanded={mostraBlocchi} data-testid="inviato-blocchi-toggle">
              {mostraBlocchi ? "Nascondi il contenuto del documento" : "Mostra il contenuto del documento"}
            </Button>
          </div>
          {mostraBlocchi ? <div className="flex flex-col gap-card">{blocchi}</div> : null}
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-card xl:grid-cols-[minmax(0,1fr)_minmax(0,440px)]">
          <div className="flex flex-col gap-card">{blocchi}</div>
          {largo ? (
            <aside className="sticky top-4 rounded-xl border border-line bg-surface p-4" aria-label="Anteprima">
              <Anteprima id={id} rev={rev} nome={nomeDelCliente} />
            </aside>
          ) : (
            <Dialog open={anteprimaAperta} onOpenChange={setAnteprimaAperta}>
              <DialogContent className="max-w-3xl" data-testid="anteprima-dialogo">
                <DialogHeader>
                  <DialogTitle>Anteprima del documento</DialogTitle>
                  <DialogDescription>Come lo vedrà il cliente, composto adesso.</DialogDescription>
                </DialogHeader>
                {anteprimaAperta ? <Anteprima id={id} rev={rev} nome={nomeDelCliente} /> : null}
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}
    </section>
  );
}

/* ================================================================= */
function BloccoCard({ def, row, readOnly, onToggle, onContenutoChange, children }) {
  const active = row?.attivo ?? true;
  const isPlaceholder = def.placeholder;
  return (
    <section
      className={cn("rounded-xl border border-line bg-surface", !active && "opacity-60")}
      data-testid={`blocco-${def.codice}`}
    >
      <header className="flex min-h-14 flex-wrap items-center gap-3 border-b border-line-subtle px-4 py-3">
        <span className="grid size-7 shrink-0 place-items-center rounded-sm bg-brand-subtle text-caption font-bold text-brand-ink" aria-hidden>
          {def.ordinamento}
        </span>
        <h3 className="min-w-0 flex-1 font-semibold text-ink-strong">{def.etichetta}</h3>
        {def.opzionale ? <StatusBadge>Opzionale</StatusBadge> : null}
        {isPlaceholder ? <StatusBadge tone="warning">Non ancora attivo</StatusBadge> : null}
        {!isPlaceholder && !readOnly ? (
          <div className="flex min-h-11 items-center gap-2">
            <Label className="flex min-h-11 items-center text-ink-2" htmlFor={`toggle-${def.codice}`}>
              Attivo
            </Label>
            <Switch
              id={`toggle-${def.codice}`}
              checked={active}
              onCheckedChange={onToggle}
              data-testid={`toggle-blocco-${def.codice}`}
            />
          </div>
        ) : null}
      </header>
      <div className="p-4 text-foreground">
        {def.descrizione ? <p className="mb-3 text-small text-ink-2">{def.descrizione}</p> : null}
        {isPlaceholder ? (
          <div
            className="rounded-md border border-dashed border-warning-line bg-warning-bg p-4 text-small text-warning-ink"
            data-testid={`placeholder-${def.codice}`}
          >
            Questo blocco non è ancora attivo. La sua posizione nel documento è già fissata.
          </div>
        ) : children ? (
          children
        ) : (
          <GenericContent contenuto={row?.contenuto} onSave={onContenutoChange} readOnly={readOnly} />
        )}
      </div>
    </section>
  );
}

function GenericContent({ contenuto, onSave, readOnly }) {
  const [txt, setTxt] = useState(contenuto?.testo || "");
  useEffect(() => setTxt(contenuto?.testo || ""), [contenuto?.testo]);
  return (
    <div className="space-y-2">
      <Textarea
        rows={3}
        value={txt}
        disabled={readOnly}
        onChange={(e) => setTxt(e.target.value)}
        onBlur={() => onSave({ ...(contenuto || {}), testo: txt })}
        placeholder="Testo del blocco…"
      />
    </div>
  );
}

function SchedaBlockContent({ prev, onPatch, readOnly, preventivoId, onTipoChanged }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-inside">
        <Label className="text-xs text-muted-foreground">Titolo</Label>
        <Input defaultValue={prev.titolo || ""} disabled={readOnly}
          onBlur={(e) => onPatch({ titolo: e.target.value })}
          data-testid="scheda-titolo" />
      </div>
      <div className="space-y-inside">
        <Label className="text-xs text-muted-foreground">Riferimento ordine</Label>
        <Input defaultValue={prev.riferimento_ordine || ""} disabled={readOnly}
          onBlur={(e) => onPatch({ riferimento_ordine: e.target.value })}
          data-testid="scheda-riferimento" />
      </div>
      <div className="sm:col-span-2">
        <TipoFornituraSelector
          preventivoId={preventivoId}
          tipoAttuale={prev.tipo_fornitura}
          readOnly={readOnly}
          onChanged={onTipoChanged}
        />
      </div>
      <div className="sm:col-span-2">
        <RegistroSelector
          preventivoId={preventivoId}
          valoreAttuale={prev.registro}
          readOnly={readOnly}
          onChanged={onTipoChanged}
        />
      </div>
    </div>
  );
}

function ProgettistaBlockContent({ prev, onPatch, readOnly }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-inside">
        <Label className="text-xs text-muted-foreground">Nome progettista</Label>
        <Input defaultValue={prev.progettista_nome || ""} disabled={readOnly}
          onBlur={(e) => onPatch({ progettista_nome: e.target.value, progettista_visibile: true })}
          data-testid="progettista-nome" />
      </div>
      <div className="space-y-inside">
        <Label className="text-xs text-muted-foreground">Studio</Label>
        <Input defaultValue={prev.progettista_studio || ""} disabled={readOnly}
          onBlur={(e) => onPatch({ progettista_studio: e.target.value })}
          data-testid="progettista-studio" />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs text-muted-foreground">Contatti</Label>
        <Input defaultValue={prev.progettista_contatti || ""} disabled={readOnly}
          onBlur={(e) => onPatch({ progettista_contatti: e.target.value })}
          data-testid="progettista-contatti" />
      </div>
    </div>
  );
}

function ComposizioneBlockContent({ contenuto, onSave, readOnly }) {
  const initial = (contenuto?.righe || []).map((r) => r.descrizione || "");
  const [righe, setRighe] = useState(initial.length ? initial : ["", "", "", "", "", ""]);

  const update = (i, v) => {
    const next = [...righe];
    next[i] = v;
    setRighe(next);
  };
  const add = () => righe.length < 8 && setRighe([...righe, ""]);
  const remove = (i) => righe.length > 6 && setRighe(righe.filter((_, idx) => idx !== i));

  const salvate = (contenuto?.righe || []).map((r) => r.descrizione || "");
  const inPari =
    (salvate.length === 0 && righe.every((d) => !d.trim())) ||
    (salvate.length === righe.length && salvate.every((d, i) => d === righe[i]));

  const save = () => {
    const cleaned = righe.map((d) => ({ descrizione: d }));
    if (cleaned.length < 6 || cleaned.length > 8) {
      toast.error("Composizione: da 6 a 8 righe, non una di più");
      return;
    }
    onSave({ righe: cleaned });
  };

  /* Uscendo da una riga, la composizione si salva da sola quando è completa:
     tutte le righe scritte. Prima si salvava SOLO col bottone, mentre ogni
     altro campo dell'editor si salva uscendo: chi scriveva le sei righe e
     pubblicava si sentiva dire «Composizione: 0 righe» con le righe a schermo. */
  const salvaSeCompleta = () => {
    if (readOnly || inPari) return;
    if (righe.length >= 6 && righe.length <= 8 && righe.every((d) => d.trim())) save();
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Vincolo: da 6 a 8 righe. Attualmente {righe.length}.
      </p>
      <div className="space-y-2">
        {righe.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
            <Input
              value={r}
              disabled={readOnly}
              onChange={(e) => update(i, e.target.value)}
              onBlur={salvaSeCompleta}
              aria-label={`Composizione, riga ${i + 1}`}
              data-testid={`comp-row-${i}`}
            />
            {!readOnly && righe.length > 6 ? (
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-muted-foreground hover:text-danger-foreground"
                data-testid={`comp-del-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {!readOnly ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            disabled={righe.length >= 8}
            data-testid="comp-add"
          >
            <Plus className="h-4 w-4 mr-1" /> Aggiungi riga
          </Button>
          <Button size="sm" onClick={save} data-testid="comp-save">
            Salva composizione
          </Button>
          {!inPari ? (
            <span className="self-center text-xs text-warning-foreground" data-testid="comp-non-salvata">
              Non ancora salvata
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ScadenzaBlockContent({ prev, onPatch, readOnly }) {
  const [preset, setPreset] = useState(prev.scadenza_preset || "");
  const [dt, setDt] = useState(prev.valido_fino || "");
  useEffect(() => {
    setPreset(prev.scadenza_preset || "");
    setDt(prev.valido_fino || "");
  }, [prev.scadenza_preset, prev.valido_fino]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "libera") {
      onPatch({ scadenza_preset: "libera" });
      return;
    }
    const days = Number(p);
    const d = new Date();
    d.setDate(d.getDate() + days);
    const iso = d.toISOString().slice(0, 10);
    setDt(iso);
    onPatch({ scadenza_preset: p, valido_fino: iso });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Preset</Label>
        <div className="grid grid-cols-4 gap-2" data-testid="scadenza-preset-group">
          {["7", "14", "21", "libera"].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              disabled={readOnly}
              data-testid={`scadenza-preset-${p}`}
              className={[
                "rounded-md border py-2 text-xs font-semibold max-md:min-h-11",
                preset === p
                  ? "border-transparent bg-brand-subtle text-brand-subtle-foreground"
                  : "bg-card text-foreground hover:bg-accent",
              ].join(" ")}
            >
              {p === "libera" ? "Data libera" : `${p} gg`}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground" htmlFor="cal-dt">Valido fino al</Label>
        <CampoData
          id="cal-dt"
          value={dt || ""}
          disabled={readOnly}
          onChange={(e) => setDt(e.target.value)}
          onBlur={() => onPatch({ valido_fino: dt, scadenza_preset: preset || "libera" })}
          testId="scadenza-date"
        />
      </div>
    </div>
  );
}

function FooterBlockContent({ prev }) {
  const [sede, setSede] = useState(null);
  useEffect(() => {
    if (!prev.sede_id) return;
    api.get("/sedi").then((r) => {
      const s = (r.data || []).find((x) => x.id === prev.sede_id);
      setSede(s);
    });
  }, [prev.sede_id]);
  if (!sede) return <p className="text-xs text-muted-foreground">Sede non impostata.</p>;
  return (
    <div className="text-sm text-foreground" data-testid="footer-content">
      <p className="font-medium">Interni d&#39;Autore Arredamenti — {sede.nome}</p>
      <p className="text-xs text-muted-foreground mt-1">
        {sede.citta} ({sede.provincia}) · Tel {sede.telefono} · {sede.email}
      </p>
    </div>
  );
}

/* ================================================================= */
function PrezzoBlockContent({ preventivoId, detail, tab, onChanged, readOnly }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [openCfg, setOpenCfg] = useState(false);
  const [openAgg, setOpenAgg] = useState(false);

  const perimetroInit = detail.preventivo.perimetro_comune || "";
  const [perim, setPerim] = useState(perimetroInit);
  useEffect(() => setPerim(perimetroInit), [perimetroInit]);

  const savePerimetro = async () => {
    if (perim === perimetroInit) return;
    try {
      const r = await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}`, { perimetro_comune: perim }));
      onChanged && onChanged(r.data);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const remConfig = async (cid) => {
    const ok = await chiedi({
      titolo: "Rimuovere questa configurazione?",
      descrizione:
        "Sparisce dalla tabella prezzi del documento, con il suo prezzo e i suoi aggiuntivi. Non si torna indietro.",
      verbo: "Rimuovi la configurazione",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.delete(`/preventivatore/${preventivoId}/configurazioni/${cid}`));
      onChanged && onChanged();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };
  const remAgg = async (aid) => {
    const ok = await chiedi({
      titolo: "Rimuovere questo aggiuntivo?",
      descrizione:
        "Il suo prezzo esce dal totale di tutte le configurazioni. Non si torna indietro.",
      verbo: "Rimuovi l'aggiuntivo",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.delete(`/preventivatore/${preventivoId}/aggiuntivi/${aid}`));
      onChanged && onChanged();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="prezzo-block">
      {dialogoConferma}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Cosa resta identico fra le configurazioni</Label>
        <Textarea
          rows={2}
          value={perim}
          disabled={readOnly}
          onChange={(e) => setPerim(e.target.value)}
          onBlur={savePerimetro}
          placeholder="Es. In tutte le configurazioni sono compresi montaggio e trasporto…"
          data-testid="prezzo-perimetro"
        />
      </div>

      <div className="rounded-md border overflow-hidden">
        <div className="bg-muted px-4 py-2.5 border-b flex items-center justify-between">
          <p className="text-eyebrow uppercase text-muted-foreground">
            Tabella prezzi generata
          </p>
          <button
            type="button"
            onClick={() => onChanged && onChanged()}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground max-md:min-h-11 max-md:px-2"
            data-testid="prezzo-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Ricalcola
          </button>
        </div>
        {tab && tab.righe && tab.righe.length ? (
          <>
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-eyebrow uppercase text-muted-foreground">
              <div className="col-span-4">Configurazione</div>
              <div className="col-span-3">Perché sceglierla</div>
              <div className="col-span-1 text-right">Config</div>
              <div className="col-span-1 text-right">+Aggiunt.</div>
              <div className="col-span-2 text-right">Totale</div>
              <div className="col-span-1 text-right">Vs base</div>
            </div>
            {tab.righe.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-12 gap-2 px-4 py-3 border-t items-center text-sm"
                data-testid={`prezzo-riga-${r.id}`}
              >
                <div className="col-span-12 sm:col-span-4 font-medium text-foreground flex items-center gap-2">
                  {r.etichetta}
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={() => remConfig(r.id)}
                      className="text-muted-foreground hover:text-danger-foreground max-md:grid max-md:size-11 max-md:place-items-center"
                      aria-label={`Togli la configurazione ${r.etichetta}`}
                      data-testid={`prezzo-del-config-${r.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
                <div className="col-span-12 sm:col-span-3 text-xs text-muted-foreground">
                  {r.perche_sceglierla || "—"}
                </div>
                <div className="col-span-4 sm:col-span-1 text-right tabular-nums text-foreground">
                  {eur(r.prezzo_configurazione)}
                </div>
                <div className="col-span-4 sm:col-span-1 text-right tabular-nums text-muted-foreground">
                  {eur(r.somma_aggiuntivi)}
                </div>
                <div className="col-span-4 sm:col-span-2 text-right tabular-nums font-semibold text-primary">
                  {eur(r.totale)}
                </div>
                <div className="col-span-12 sm:col-span-1 text-right tabular-nums text-xs text-muted-foreground">
                  {r.differenza_vs_base === 0 ? "base" : `+${eur(r.differenza_vs_base)}`}
                </div>
              </div>
            ))}
            <div className="px-4 py-2 border-t bg-muted text-xs text-muted-foreground">
              Somma aggiuntivi applicata a tutte le configurazioni: <span className="font-semibold">{eur(tab.somma_aggiuntivi)}</span>
            </div>
          </>
        ) : (
          <div className="px-4 py-6 text-sm text-muted-foreground text-center">
            Nessuna configurazione. Aggiungine almeno una per far comparire la tabella.
          </div>
        )}
      </div>

      {!readOnly ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenCfg(true)}
            data-testid="btn-add-config"
          >
            <Plus className="h-4 w-4 mr-1" /> Aggiungi configurazione
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenAgg(true)}
            data-testid="btn-add-aggiuntivo"
          >
            <Plus className="h-4 w-4 mr-1" /> Aggiungi pacchetto aggiuntivo
          </Button>
        </div>
      ) : null}

      {tab && tab.aggiuntivi?.length ? (
        <div className="rounded-md border divide-y divide-border">
          <div className="px-4 py-2 text-eyebrow uppercase text-muted-foreground bg-muted">
            Pacchetti aggiuntivi
          </div>
          {tab.aggiuntivi.map((a) => (
            <div key={a.id} className="px-4 py-2 flex items-center gap-3 text-sm" data-testid={`prezzo-agg-${a.id}`}>
              <div className="flex-1">
                <p className="font-medium text-foreground">{a.nome}</p>
                <p className="text-xs text-muted-foreground">{a.descrizione || "—"}</p>
              </div>
              <div className="tabular-nums font-medium">{eur(a.prezzo)}</div>
              {!readOnly ? (
                <button
                  onClick={() => remAgg(a.id)}
                  className="text-muted-foreground hover:text-danger-foreground"
                  data-testid={`prezzo-del-agg-${a.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <ConfigDialog
        open={openCfg}
        onOpenChange={setOpenCfg}
        preventivoId={preventivoId}
        livelloDefault={detail.preventivo.livello_ricarico_default || "M5"}
        onCreated={() => onChanged && onChanged()}
        tipo="configurazione"
      />
      <ConfigDialog
        open={openAgg}
        onOpenChange={setOpenAgg}
        preventivoId={preventivoId}
        livelloDefault={detail.preventivo.livello_ricarico_default || "M5"}
        onCreated={() => onChanged && onChanged()}
        tipo="aggiuntivo"
      />
    </div>
  );
}

/* ================================================================= */
function ConfigDialog({ open, onOpenChange, preventivoId, livelloDefault, onCreated, tipo }) {
  const isAgg = tipo === "aggiuntivo";
  const [form, setForm] = useState({
    etichetta: "",
    nome: "",
    descrizione: "",
    perche_sceglierla: "",
    totale_listino: "",
    totale_promotion: "0",
    livello_ricarico: livelloDefault,
  });
  const [levels, setLevels] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/calcolatore/parametri").then((r) => setLevels(r.data.ricarichi || [])).catch(() => {});
      setForm((f) => ({ ...f, livello_ricarico: livelloDefault }));
    }
  }, [open, livelloDefault]);

  const [erroreImporti, setErroreImporti] = useState(null);

  const submit = async () => {
    /* Gli importi si leggono come li scrive una persona («9.500,00»). Prima
       partiva la stringa com'era: il server la rifiutava con un elenco di
       errori, l'elenco finiva dentro il messaggio e la pagina cadeva. La
       configurazione — cioè il prezzo — non si salvava. Difetto della call. */
    const listino = leggiImporto(form.totale_listino);
    const promozione = form.totale_promotion?.trim() ? leggiImporto(form.totale_promotion) : 0;
    if (listino === null || promozione === null) {
      setErroreImporti(
        listino === null
          ? "Scrivi il totale listino come numero, per esempio 9.500,00."
          : "Scrivi il totale promotion come numero, oppure lascialo a 0.",
      );
      return;
    }
    setErroreImporti(null);
    setBusy(true);
    try {
      const body = isAgg
        ? {
            nome: form.nome,
            descrizione: form.descrizione,
            totale_listino: listino,
            totale_promotion: promozione,
            livello_ricarico: form.livello_ricarico,
          }
        : {
            etichetta: form.etichetta,
            perche_sceglierla: form.perche_sceglierla,
            totale_listino: listino,
            totale_promotion: promozione,
            livello_ricarico: form.livello_ricarico,
          };
      const url = isAgg
        ? `/preventivatore/${preventivoId}/aggiuntivi`
        : `/preventivatore/${preventivoId}/configurazioni`;
      await registraSalvataggio(api.post(url, body));
      onOpenChange(false);
      setForm({
        etichetta: "",
        nome: "",
        descrizione: "",
        perche_sceglierla: "",
        totale_listino: "",
        totale_promotion: "0",
        livello_ricarico: livelloDefault,
      });
      onCreated && onCreated();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfigDialogUI
      open={open}
      onOpenChange={onOpenChange}
      title={isAgg ? "Nuovo pacchetto aggiuntivo" : "Nuova configurazione"}
      onSubmit={submit}
      busy={busy}
    >
      {isAgg ? (
        <>
          <FieldRow>
            <FieldLabel>Nome</FieldLabel>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cfg-nome" />
          </FieldRow>
          <FieldRow>
            <FieldLabel>Descrizione</FieldLabel>
            <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="cfg-descr" />
          </FieldRow>
        </>
      ) : (
        <>
          <FieldRow>
            <FieldLabel>Etichetta</FieldLabel>
            <Input value={form.etichetta} onChange={(e) => setForm({ ...form, etichetta: e.target.value })}
              placeholder="es. Anta Rovere · Top Quarzo" data-testid="cfg-etichetta" />
          </FieldRow>
          <FieldRow>
            <FieldLabel>Perché sceglierla</FieldLabel>
            <Input value={form.perche_sceglierla} onChange={(e) => setForm({ ...form, perche_sceglierla: e.target.value })}
              placeholder="Frase che aiuta il cliente a decidere" data-testid="cfg-perche" />
          </FieldRow>
        </>
      )}
      <div className="grid grid-cols-2 gap-3">
        <FieldRow>
          <FieldLabel>Totale listino</FieldLabel>
          <Input inputMode="decimal" value={form.totale_listino}
            onChange={(e) => setForm({ ...form, totale_listino: e.target.value })}
            placeholder="es. 9.500,00" data-testid="cfg-listino" />
        </FieldRow>
        <FieldRow>
          <FieldLabel>Totale promotion</FieldLabel>
          <Input inputMode="decimal" value={form.totale_promotion}
            onChange={(e) => setForm({ ...form, totale_promotion: e.target.value })}
            data-testid="cfg-promo" />
        </FieldRow>
      </div>
      {erroreImporti ? (
        <p className="text-xs text-danger-foreground" role="alert" data-testid="cfg-errore-importi">
          {erroreImporti}
        </p>
      ) : null}
      <FieldRow>
        <FieldLabel>Livello di prezzo</FieldLabel>
        <Select value={form.livello_ricarico} onValueChange={(v) => setForm({ ...form, livello_ricarico: v })}>
          <SelectTrigger data-testid="cfg-livello"><SelectValue /></SelectTrigger>
          <SelectContent>
            {levels.map((l) => (
              // Solo il nome del livello: la percentuale di ricarico non compare
              // nel preventivatore, che si usa anche davanti al cliente.
              <SelectItem key={l.livello} value={l.livello}>{l.livello}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
    </ConfigDialogUI>
  );
}

function FieldRow({ children }) { return <div className="space-y-inside">{children}</div>; }
function FieldLabel({ children }) { return <Label className="text-xs text-muted-foreground">{children}</Label>; }

function ConfigDialogUI({ open, onOpenChange, title, onSubmit, busy, children }) {
  // Stesso aggancio di DialogFrame in BlocchiFiduciaPagamenti.js: la descrizione
  // c'era già a schermo ma non era LEGATA al dialogo via aria-describedby.
  const idDescrizione = useId();
  return (
    <div>
      {open ? (
        <div className="fixed inset-0 z-50 bg-foreground/40 grid place-items-center p-4">
          <Card
            role="dialog"
            aria-modal="true"
            aria-label={title}
            aria-describedby={idDescrizione}
            className="w-full max-w-lg p-card space-y-4"
          >
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            <p id={idDescrizione} className="text-sm text-muted-foreground">
              Compila i campi e salva: la configurazione entra nella tabella del prezzo.
            </p>
            <div className="space-y-3">{children}</div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button onClick={onSubmit} disabled={busy} data-testid="cfg-submit">
                {busy ? "Salvataggio…" : "Salva"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
